import { EnumSelectField } from '@mews-commander/form';
import { FormCheckboxField, FormInputField, LocalizedText, useLocalizeText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { CompoundField, IconName, Body, Stack, Typography } from '@optimus-web/core';
import { LocalizedSubsectionTitle, TextWithLinks, TooltippedIcon } from '@mews-commander/ui';
import {
    SmsUsaRegistrationBusinessIndustry,
    SmsUsaRegistrationBusinessRegionOfOperation,
    SmsUsaRegistrationBusinessRegistrationIdentifier,
    SmsUsaRegistrationBusinessType,
    SmsUsaRegistrationJobPosition,
} from '@mews-commander/data-access';
import { BrandRegistrationAddressFields } from './BrandRegistrationAddressFields';

interface BrandRegistrationFormFieldsProps {
    termsAndConditionsUrl: string;
}

export const BrandRegistrationFormFields = ({ termsAndConditionsUrl }: BrandRegistrationFormFieldsProps) => {
    const localizeText = useLocalizeText();

    const customerProfile = (
        <Stack vertical spacing="200">
            <LocalizedSubsectionTitle textKey={TextKey.BusinessRegistration} />

            <Body size="small">
                <LocalizedText textKey={TextKey.SmsBrandRegistrationBusinessFieldsMessage} />
            </Body>

            <FormInputField
                label={<LocalizedText textKey={TextKey.RegisteredBusinessName} />}
                name="businessName"
                placeholder={<LocalizedText textKey={TextKey.RegisteredBusinessNamePlaceholder} />}
                required
            />

            <BrandRegistrationAddressFields />

            <EnumSelectField
                enumClass={SmsUsaRegistrationBusinessRegionOfOperation}
                enumPrefix="SmsUsaRegistrationBusinessRegionOfOperation"
                label={<LocalizedText textKey={TextKey.RegionsOfOperation} />}
                name="businessRegionsOfOperation"
                captionIconWithTooltip={
                    <TooltippedIcon
                        size="small"
                        value={IconName.Info}
                        tooltip={<LocalizedText textKey={TextKey.BusinessRegionsOfOperationOneOrMoreMessage} />}
                    />
                }
                multiselect
                required
                showEmptyValue={false}
            />

            <EnumSelectField
                enumClass={SmsUsaRegistrationBusinessType}
                enumPrefix="SmsUsaRegistrationBusinessType"
                label={<LocalizedText textKey={TextKey.BusinessType} />}
                name="businessType"
                required
                showEmptyValue={false}
            />

            <EnumSelectField
                enumClass={SmsUsaRegistrationBusinessRegistrationIdentifier}
                enumPrefix="SmsUsaRegistrationBusinessRegistrationIdentifier"
                label={<LocalizedText textKey={TextKey.BusinessRegistrationIdentifier} />}
                name="businessRegistrationIdentifier"
                required
                showEmptyValue={false}
            />

            <FormInputField
                label={<LocalizedText textKey={TextKey.BusinessRegistrationNumber} />}
                name="businessRegistrationNumber"
                required
            />

            <EnumSelectField
                enumClass={SmsUsaRegistrationBusinessIndustry}
                enumPrefix="SmsUsaRegistrationBusinessIndustry"
                label={<LocalizedText textKey={TextKey.Industry} />}
                name="businessIndustry"
                required
                showEmptyValue={false}
            />

            <FormInputField
                label={<LocalizedText textKey={TextKey.WebsiteUrl} />}
                name="websiteUrl"
                required
                type="url"
            />
        </Stack>
    );

    const businessContact = (
        <Stack vertical spacing="200">
            <LocalizedSubsectionTitle textKey={TextKey.BusinessContact} />
            <FormInputField label={<LocalizedText textKey={TextKey.FirstName} />} name="firstName" required />
            <FormInputField label={<LocalizedText textKey={TextKey.LastName} />} name="lastName" required />
            <CompoundField
                name="jobTitleAndPosition"
                label={<LocalizedText textKey={TextKey.JobTitleAndPosition} />}
                required
            >
                <FormInputField
                    label={<LocalizedText textKey={TextKey.JobTitleShort} />}
                    name="jobTitle"
                    placeholder={<LocalizedText textKey={TextKey.JobTitleShort} />}
                    required
                />
                <EnumSelectField
                    enumClass={SmsUsaRegistrationJobPosition}
                    enumPrefix="SmsUsaRegistrationJobPosition"
                    label={<LocalizedText textKey={TextKey.JobPositionShort} />}
                    name="jobPosition"
                    placeholder={<LocalizedText textKey={TextKey.JobPositionShort} />}
                    required
                    showEmptyValue={false}
                />
            </CompoundField>
            <FormInputField
                label={<LocalizedText textKey={TextKey.PhoneNumber} />}
                name="phoneNumber"
                required
                type="tel"
            />
            <FormInputField label={<LocalizedText textKey={TextKey.Email} />} name="email" required type="email" />
            <FormCheckboxField name="termsAndConditions" required>
                <Typography textStyle="bodyLarge" as="span">
                    <TextWithLinks
                        links={[
                            {
                                id: 'termsAndConditions',
                                url: termsAndConditionsUrl,
                                text: localizeText({ textKey: TextKey.TermsAndConditions }),
                            },
                        ]}
                        text={localizeText({ textKey: TextKey.ConfirmReadAndAcceptTermsAndConditions })}
                    />
                </Typography>
            </FormCheckboxField>
        </Stack>
    );

    return (
        <Stack vertical spacing="400">
            {customerProfile}
            {businessContact}
        </Stack>
    );
};
